import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

// Dashboards an AI agent built over the connector (server/dashboardDrafts.js).
// They land as DRAFTS nobody sees until a person previews and publishes them.
// Same component on both surfaces via the scope prop (ApiKeysCard pattern):
// 'admin-client' (Admin → client → Integrations) | 'my' (Settings → Integrations).
export default function DashboardDraftsCard({ entityId, scope = 'my' }) {
  const [ctx, setCtx] = useState(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState('');
  const [pick, setPick] = useState({}); // draftId → { suiteId, setId }
  const [done, setDone] = useState(null); // { title, path, set }

  const load = () => api.listAiDashboardDrafts(entityId, scope).then(setCtx).catch(() => setDenied(true));
  useEffect(() => { setCtx(null); setDenied(false); setDone(null); load(); }, [entityId, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  if (denied || !ctx) return null;
  if (scope === 'my' && !ctx.drafts.length) return null; // nothing to review — keep Settings quiet

  const publish = async (d) => {
    const p = pick[d.id] || {};
    setBusy(d.id);
    try {
      const r = await api.publishAiDashboardDraft(entityId, d.id, { suiteId: p.suiteId || d.event?.suiteId, setId: p.setId || undefined }, scope);
      setDone({ title: d.title, path: r.path, set: r.set?.name });
      await load();
    } catch (e) { alert('Couldn’t publish: ' + e.message); }
    setBusy('');
  };
  const discard = async (d) => {
    if (!window.confirm(`Discard the draft “${d.title}”? This can’t be undone.`)) return;
    setBusy(d.id);
    try { await api.discardAiDashboardDraft(entityId, d.id, scope); await load(); }
    catch (e) { alert('Couldn’t discard: ' + e.message); }
    setBusy('');
  };
  const setPickFor = (id, patch) => setPick((m) => ({ ...m, [id]: { ...(m[id] || {}), ...patch } }));

  return (
    <div style={card}>
      <div style={title}>🧱 Draft dashboards</div>
      <p style={sub}>
        Dashboards built by a connected AI agent (Claude, ChatGPT) land here as drafts — nobody sees them until you
        preview one with live data and publish it into an event.
        {scope === 'admin-client' && !ctx.enabled && <> <b>Switched off for this client</b> (Product → Flags → “Build dashboards via the AI connector”).</>}
      </p>

      {done && (
        <div style={doneBox}>
          ✅ Published “{done.title}”{done.set ? ` into ${done.set}` : ''}. <Link to={done.path} style={{ color: 'var(--brand)', fontWeight: 600 }}>Open it →</Link>
        </div>
      )}

      {!ctx.drafts.length ? (
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>No drafts waiting.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {ctx.drafts.map((d) => {
            const p = pick[d.id] || {};
            return (
              <div key={d.id} style={row}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{d.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    {d.tileCount} tile{d.tileCount === 1 ? '' : 's'}{d.event ? ` · ${d.event.name}` : ''}
                    {d.createdVia ? ` · built via ${d.createdVia === 'claude' ? 'Claude' : d.createdVia === 'chatgpt' ? 'ChatGPT' : 'the API'}` : ''}
                    {d.createdAt ? ` · ${new Date(d.createdAt).toLocaleDateString()}` : ''}
                  </div>
                </div>
                <div style={pickers}>
                  {ctx.suites.length > 1 && (
                    <select style={select} value={p.suiteId || d.event?.suiteId || ''} onChange={(e) => setPickFor(d.id, { suiteId: e.target.value })} aria-label="Event to publish into">
                      {ctx.suites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  )}
                  <select style={select} value={p.setId || ''} onChange={(e) => setPickFor(d.id, { setId: e.target.value })} aria-label="Set to publish into">
                    <option value="">Set: Built with AI</option>
                    {ctx.sets.filter((s) => s.name !== 'Built with AI').map((s) => <option key={s.id} value={s.id}>Set: {s.name}</option>)}
                  </select>
                </div>
                <div style={actions}>
                  <Link to={d.reviewPath} style={{ ...ghostBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Preview</Link>
                  <button style={btn} disabled={busy === d.id} onClick={() => publish(d)}>{busy === d.id ? '…' : 'Publish'}</button>
                  <button style={dangerBtn} disabled={busy === d.id} onClick={() => discard(d)}>Discard</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Shown on the dashboard itself while it is still a draft (ViewPage) — the
// natural place to decide: you're looking at the live numbers.
export function DraftDashboardBanner({ dashboard, isAdmin }) {
  const [state, setState] = useState(''); // '' | 'busy' | 'published'
  const entityId = dashboard.ownerEntityId;
  const scope = isAdmin ? 'admin-client' : 'my';
  const publish = async () => {
    setState('busy');
    try { await api.publishAiDashboardDraft(entityId, dashboard.id, {}, scope); setState('published'); }
    catch (e) { alert('Couldn’t publish: ' + e.message); setState(''); }
  };
  const discard = async () => {
    if (!window.confirm(`Discard the draft “${dashboard.title}”? This can’t be undone.`)) return;
    try { await api.discardAiDashboardDraft(entityId, dashboard.id, scope); window.location.assign('/'); }
    catch (e) { alert('Couldn’t discard: ' + e.message); }
  };
  if (state === 'published') {
    return <div className="no-print" style={{ ...doneBox, marginBottom: 12 }}>✅ Published — it’s now in this event’s “Built with AI” set for everyone with access.</div>;
  }
  return (
    <div className="no-print" style={banner}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13.5 }}>🧱 Draft — built by an AI agent</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>Only you can see this. Check the numbers, then publish it for your team or discard it.</div>
      </div>
      <div style={actions}>
        <button style={btn} disabled={state === 'busy'} onClick={publish}>{state === 'busy' ? 'Publishing…' : 'Publish'}</button>
        <button style={dangerBtn} onClick={discard}>Discard</button>
      </div>
    </div>
  );
}

const card = { background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12, padding: 16, marginTop: 16 };
const title = { fontSize: 14, fontWeight: 700, marginBottom: 6 };
const sub = { fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' };
const row = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px' };
const pickers = { display: 'flex', flexWrap: 'wrap', gap: 8, flex: '1 1 220px' };
const actions = { display: 'flex', flexWrap: 'wrap', gap: 8 };
const select = { flex: '1 1 160px', minWidth: 0, minHeight: 40, border: '1px solid var(--hairline)', borderRadius: 8, padding: '8px 10px', fontSize: 13, background: 'var(--card)', color: 'inherit' };
const btn = { border: '1px solid var(--hairline)', background: 'var(--brand)', color: '#fff', borderRadius: 980, padding: '9px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', minHeight: 40 };
const ghostBtn = { ...btn, background: 'transparent', color: 'var(--muted)' };
const dangerBtn = { ...ghostBtn, color: 'var(--danger, #dc2626)' };
const doneBox = { border: '1px solid var(--brand)', borderRadius: 10, padding: 12, marginBottom: 12, fontSize: 13 };
const banner = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px dashed var(--brand)', background: 'rgba(var(--brand-rgb),0.06)', borderRadius: 12, padding: '12px 14px', marginBottom: 12 };
